import DOMPurify from 'dompurify';
import { Marked, type Tokens } from 'marked';

/**
 * Markdown → sanitized HTML for settled assistant blocks (SPEC §10).
 *
 * Agent output is untrusted text: a tool result may have carried it in from a
 * file, a web page or an MCP server. Two independent guards, so neither is a
 * single point of failure:
 *
 *  1. the `html` renderer is overridden to ESCAPE raw HTML instead of emitting
 *     it — `<script>alert(1)</script>` in the source becomes visible text, not
 *     markup (marked's default passes raw HTML straight through);
 *  2. DOMPurify then sanitizes the result against an explicit tag/attribute
 *     allowlist and a URI scheme allowlist, so `javascript:` hrefs, event
 *     handlers and embedded objects cannot survive even if (1) regressed.
 *
 * Live tails are deliberately NOT parsed here: the streaming tail renders as
 * plain text and only settled blocks are converted, which is what keeps
 * streaming O(n) instead of re-parsing a growing block on every delta.
 */

function escapeHtml(raw: string): string {
  return raw
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

const marked = new Marked(
  { gfm: true, breaks: true, async: false },
  {
    renderer: {
      // Covers both block-level HTML and inline tags: the parser routes both
      // token kinds through this method.
      html(token: Tokens.HTML | Tokens.Tag): string {
        return escapeHtml(token.raw);
      },
    },
  },
);

const ALLOWED_TAGS = [
  'p',
  'br',
  'strong',
  'em',
  'del',
  'code',
  'pre',
  'blockquote',
  'ul',
  'ol',
  'li',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'a',
  'hr',
  'table',
  'thead',
  'tbody',
  'tr',
  'th',
  'td',
  'span',
];

const ALLOWED_ATTR = ['href', 'title', 'colspan', 'rowspan', 'align'];

let hookInstalled = false;

function installHook(): void {
  if (hookInstalled) return;
  hookInstalled = true;
  DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    if (node.tagName === 'A') {
      node.setAttribute('target', '_blank');
      node.setAttribute('rel', 'noopener noreferrer nofollow');
    }
  });
}

export function renderMarkdown(source: string): string {
  installHook();
  const html = marked.parse(source, { async: false });
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    ALLOW_DATA_ATTR: false,
    ALLOW_ARIA_ATTR: false,
    ALLOWED_URI_REGEXP: /^(?:https?:|mailto:|#)/i,
    FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'form', 'input', 'img', 'svg'],
    FORBID_ATTR: ['style', 'srcset', 'formaction', 'onerror', 'onload', 'onclick'],
    KEEP_CONTENT: true,
  });
}
